import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { wmsItemService } from "services";
import { WmsItem } from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type WmsItemListKey = ["wms-items"];
type WmsItemDetailKey = ["wms-items", "detail", number | string];

const wmsItemKeys = {
  all: ["wms-items"] as const,
  list: (): WmsItemListKey => ["wms-items"],
  detail: (id: number | string): WmsItemDetailKey => [
    "wms-items",
    "detail",
    id,
  ],
};

export function useWmsItem() {
  const queryClient = useQueryClient();

  const fetchList = (enabled = true) =>
    useQuery({
      queryKey: wmsItemKeys.list(),
      queryFn: () => wmsItemService.list(),
      enabled,
      select: (data) => data ?? [],
      staleTime: 5 * 60 * 1000,
    });

  const fetchDetail = (id: number | string, enabled = true) =>
    useQuery({
      queryKey: wmsItemKeys.detail(id),
      queryFn: () => wmsItemService.show(id),
      enabled: Boolean(id) && enabled,
    });

  const createItem = useMutation({
    mutationFn: (payload: WmsItem) => wmsItemService.create(payload),
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Tạo hàng hóa thành công");
      queryClient.invalidateQueries({ queryKey: wmsItemKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo hàng hóa thất bại");
      console.error("createWmsItem failed", err);
    },
  });

  const updateItem = useMutation({
    mutationFn: (payload: WmsItem & { id: number | string }) =>
      wmsItemService.update(payload.id, payload),
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Cập nhật hàng hóa thành công");
      queryClient.invalidateQueries({ queryKey: wmsItemKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật hàng hóa thất bại");
      console.error("updateWmsItem failed", err);
    },
  });

  const deleteItem = useMutation({
    mutationFn: (id: number | string) => wmsItemService.destroy(id),
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Xóa hàng hóa thành công");
      queryClient.invalidateQueries({ queryKey: wmsItemKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xóa hàng hóa thất bại");
      console.error("deleteWmsItem failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createItem,
    updateItem,
    deleteItem,
  };
}
